// KakaoTalk opens links in a browser of its own, where a web app cannot be
// installed and gets no notifications (operations guide 9.1). On the first
// open there both apps (talk, tensor) hand their address to the phone's
// browser with KakaoTalk's openExternal, and show a guide for when the page
// is still in KakaoTalk afterwards (ui/InApp.tsx). Navigation is not under
// the CSP (deploy/Caddyfile), so the kakaotalk: address needs nothing there.

const TRIED_KEY = 'hm.kakaoOut' // when a page was last sent out (localStorage)
const STAY_KEY = 'hm.kakaoStay' // chose to go on in KakaoTalk (sessionStorage)

/** Not sent out again within this long: were KakaoTalk to open the address
    in itself once more, it would go round and round. */
export const KAKAO_AGAIN_MS = 60_000

/** KakaoTalk's own browser says so in its user agent ("…;KAKAOTALK 2610420"). */
export function isKakaoTalk(ua: string): boolean {
  return /KAKAOTALK/i.test(ua)
}

/** The address that asks KakaoTalk to open href in the phone's browser. */
export function kakaoExternalUrl(href: string): string {
  return `kakaotalk://web/openExternal?url=${encodeURIComponent(href)}`
}

/** Whether to send the page out now: in KakaoTalk, unless this person chose
    to stay or it was tried a moment ago (lastTry: when, ms; null: never). */
export function shouldLeaveKakao(ua: string, now: number, lastTry: number | null, stayed: boolean): boolean {
  if (!isKakaoTalk(ua) || stayed) return false
  return lastTry === null || now < lastTry || now - lastTry >= KAKAO_AGAIN_MS
}

function read(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key)
  } catch {
    return null // storage off (private mode)
  }
}

function write(storage: () => Storage, key: string, value: string) {
  try {
    storage().setItem(key, value)
  } catch {
    // storage off: nothing to remember it by
  }
}

/** Asks KakaoTalk to open this page in the phone's browser (Chrome). */
export function openOutside() {
  location.href = kakaoExternalUrl(location.href)
}

/** The first thing an app does, before anything reads or changes the
    address (/join#<token> keeps its token): in KakaoTalk, sends the page out
    (at most once a minute) and returns true, for the guide. False elsewhere,
    and in KakaoTalk once this person chose to go on there. */
export function leaveKakaoTalk(): boolean {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent
  if (!isKakaoTalk(ua)) return false
  const stayed = read(() => sessionStorage, STAY_KEY) === '1'
  if (stayed) return false
  const last = Number(read(() => localStorage, TRIED_KEY))
  const now = Date.now()
  if (shouldLeaveKakao(ua, now, Number.isFinite(last) && last > 0 ? last : null, stayed)) {
    write(() => localStorage, TRIED_KEY, String(now))
    openOutside()
  }
  return true
}

/** Goes on in KakaoTalk: no more guide or sending out in this tab. */
export function stayInKakaoTalk() {
  write(() => sessionStorage, STAY_KEY, '1')
}
